import { Col, Input, Row, Select, Switch } from "antd";
import { useStoreScenarioMutation } from "api/TenantSlug/ConnectionCreator/hooks";
import { AxiosError } from "axios";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { FormStructureSelectSearchable } from "components/UI/Selects/FormStructureSelectSearchable/FormStructureSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser.ts";
import { TOAST_TYPES } from "localConstants.ts";
import { FC, MutableRefObject, useEffect, useMemo, useRef } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { ConnectionCreatorScenarioCategory } from "types/enums";
import { ApiErrorResponse, FormId } from "types/models.ts";
import { showCustomToast } from "utils/toast.ts";

import { AddFormStructureScenarioFormData, schema } from "./formSchema";

interface AddFormStructureScenarioFormProps {
    onSubmit?: () => void;
}

interface AddFormStructureScenarioFormContentProps {
    handleErrorsRef: MutableRefObject<((error: AxiosError<ApiErrorResponse>) => void) | null>;
}

const AddFormStructureScenarioFormContent: FC<AddFormStructureScenarioFormContentProps> = ({ handleErrorsRef }) => {
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tCC } = usePaldockTranslation("components.connection-creator");
    const { control, fieldNames, handleErrorsFromErrorResponse } = useGeneralForm<AddFormStructureScenarioFormData>();

    useEffect(() => {
        handleErrorsRef.current = handleErrorsFromErrorResponse;
    }, [handleErrorsFromErrorResponse, handleErrorsRef]);

    const categoryOptions = useMemo(
        () => [
            {
                value: ConnectionCreatorScenarioCategory.StructureValidate,
                label: tCC("enums.scenario_category.structure_validate"),
            },
            {
                value: ConnectionCreatorScenarioCategory.StructureModify,
                label: tCC("enums.scenario_category.structure_modify"),
            },
            {
                value: ConnectionCreatorScenarioCategory.StructureAutocomplete,
                label: tCC("enums.scenario_category.structure_autocomplete"),
            },
        ],
        [tCC]
    );

    return (
        <div className={"w-100 p-[24px] bg-[#F5F5F5] border border-[#D9D9D9] rounded-[6px] border-dashed"}>
            <Row gutter={[12, 12]}>
                <Col span={24}>
                    <FormItem control={control} name={fieldNames.title.name} label={tFields("name.label")} required>
                        <Input placeholder={tFields("name.placeholder")} />
                    </FormItem>
                </Col>
            </Row>
            <Row gutter={[12, 12]}>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.category.name}
                        label={tCC("enums.scenario_category.label")}
                        required
                    >
                        <Select options={categoryOptions} placeholder={tCC("enums.scenario_category.placeholder")} />
                    </FormItem>
                </Col>
            </Row>
            <Row gutter={[12, 12]}>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.is_enabled.name}
                        label={tFields("status.label")}
                        valuePropName="checked"
                    >
                        <Switch />
                    </FormItem>
                </Col>
            </Row>
            <Row gutter={[12, 12]}>
                <Col span={24}>
                    <FormItem control={control} name="form_structure_ids" label={tFields("form_structure.label")}>
                        <FormStructureSelectSearchable
                            mode="multiple"
                            allowClear
                            placeholder={tFields("form_structure.placeholder")}
                        />
                    </FormItem>
                </Col>
            </Row>
        </div>
    );
};

export const AddFormStructureScenarioForm: FC<AddFormStructureScenarioFormProps> = ({ onSubmit }) => {
    const { t } = usePaldockTranslation("forms.add-scenario");
    const { t: tC } = usePaldockTranslation("common");
    const { getActiveTenant } = useUser();
    const { mutate: createScenario } = useStoreScenarioMutation();
    const handleErrorsRef = useRef<((error: AxiosError<ApiErrorResponse>) => void) | null>(null);

    const defaultValues: AddFormStructureScenarioFormData = {
        title: "",
        is_enabled: true,
        form_structure_ids: [],
        category: null,
    };

    const handleSubmit = (values: AddFormStructureScenarioFormData) => {
        createScenario(
            {
                title: values.title,
                is_enabled: values.is_enabled,
                category: values.category,
                form_structure_ids: values.form_structure_ids ?? [],
            },
            {
                onSuccess: (response) => {
                    if (response && response.scenario) {
                        showCustomToast(
                            {
                                title: t("messages.success"),
                                buttons: [
                                    {
                                        label: tC("open"),
                                        href:
                                            response?.scenario?.id && getActiveTenant()?.slug_name
                                                ? `/${getActiveTenant()?.slug_name}/tool/validation/${response.scenario.id}`
                                                : undefined,
                                    },
                                ],
                            },
                            TOAST_TYPES.SUCCESS
                        );
                    }
                    onSubmit?.();
                },
                onError: (error) => {
                    toast.error(t("messages.error", { message: error.message }));
                    handleErrorsRef.current?.(error);
                },
            }
        );
    };

    return (
        <GeneralFormProvider<AddFormStructureScenarioFormData>
            id={FormId.AddFormStructureScenarioForm}
            schema={schema}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
        >
            <AddFormStructureScenarioFormContent handleErrorsRef={handleErrorsRef} />
        </GeneralFormProvider>
    );
};
